import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, Mail, ArrowLeft, Pencil, MapPin, ClipboardList, GitMerge, LayoutGrid, Car, History, Stethoscope, Send, Wallet, Archive, ArchiveRestore } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import AddVehicleModal from './components/AddVehicleModal';
import EditCustomerModal from './components/EditCustomerModal';
import CustomerEmailModal from './components/CustomerEmailModal';
import ServiceHistoryModal from './ServiceHistoryModal';
import MergeCustomerModal from './MergeCustomerModal';
import ArchiveCustomerModal from './components/ArchiveCustomerModal';
import { DiagnosticModal, type DiagnosticContext } from '../Workshop/Diagnostics/DiagnosticModal';
import { OverviewTab } from './tabs/OverviewTab';
import { VehiclesTab } from './tabs/VehiclesTab';
import { ActivityTab } from './tabs/ActivityTab';
import { DiagnosticsTab } from './tabs/DiagnosticsTab';
import { CommunicationTab } from './tabs/CommunicationTab';
import { ServiceHistoryTab } from './tabs/ServiceHistoryTab';
import { reportError } from '../../../lib/errors';
import type {
    CustomerVehicle,
    CustomerJob,
    CustomerDiagnosticReport,
    CustomerEmailLogRow,
    CustomerServiceRecord,
} from './tabs/types';

type TabId = 'overview' | 'vehicles' | 'activity' | 'diagnostics' | 'communication' | 'service_history';

export default function CustomerProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeOrgId } = useVertical();

    // State
    const [customer, setCustomer] = useState<any>(null);
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
    const [jobs, setJobs] = useState<CustomerJob[]>([]);
    const [serviceHistory, setServiceHistory] = useState<CustomerServiceRecord[]>([]);
    const [financials, setFinancials] = useState({ paid: 0, owed: 0 });
    const [loading, setLoading] = useState(true);
    const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [businessEmail, setBusinessEmail] = useState<string | undefined>(undefined);
    const [businessName, setBusinessName] = useState<string | undefined>(undefined);
    const [serviceHistoryOpen, setServiceHistoryOpen] = useState(false);
    const [serviceHistoryVehicleId, setServiceHistoryVehicleId] = useState<string | undefined>(undefined);
    const [isMergeOpen, setIsMergeOpen] = useState(false);
    // Everything that's been said to, and found on, this customer.
    const [diagnostics, setDiagnostics] = useState<CustomerDiagnosticReport[]>([]);
    const [emails, setEmails] = useState<CustomerEmailLogRow[]>([]);
    const [diagnosticContext, setDiagnosticContext] = useState<DiagnosticContext | undefined>(undefined);
    const [diagnosticOpen, setDiagnosticOpen] = useState(false);

    // Active tab — defaults to the condensed Overview so a returning mechanic
    // gets their bearings without opening every section.
    const [activeTab, setActiveTab] = useState<TabId>('overview');

    const openDiagnostic = (r: CustomerDiagnosticReport) => {
        setDiagnosticContext({
            reportId: r.id,
            customerId: r.customer_id ?? undefined,
            vehicleId: r.vehicle_id ?? undefined,
            appointmentId: r.appointment_id ?? undefined,
            customerName: r.customer_name ?? undefined,
            vehicleVrm: r.vehicle_vrm ?? undefined,
            vehicleDesc: r.vehicle_desc ?? undefined,
            findings: r.findings ?? [],
            lineItems: r.line_items ?? [],
            summary: r.summary ?? undefined,
            status: r.status,
            createdAt: r.created_at,
            quoteId: r.quote_id,
        });
        setDiagnosticOpen(true);
    };

    const openServiceHistory = (vehicleId?: string) => {
        setServiceHistoryVehicleId(vehicleId);
        setServiceHistoryOpen(true);
    };
    // Vehicles that actually have completed (paid) service records.
    const vehiclesWithHistory = new Set(
        serviceHistory.map((r) => r.vehicle_id).filter((v): v is string => Boolean(v))
    );

    // Which template (and vehicle) the composer opens on. Empty = show the
    // template picker.
    const [emailPrefill, setEmailPrefill] = useState<{ templateId?: string; vehicleId?: string }>({});

    const openEmail = (templateId?: string, vehicleId?: string) => {
        if (!customer?.email) {
            toast.error('This customer has no email address on file.');
            return;
        }
        setEmailPrefill({ templateId, vehicleId });
        setIsEmailModalOpen(true);
    };

    // Save the MOT due date / reminder toggle straight onto the vehicle row.
    const handleMotUpdate = async (
        vehicleId: string,
        patch: { mot_expiry?: string | null; mot_reminder_enabled?: boolean }
    ) => {
        const { error } = await supabase.from('vehicles').update(patch).eq('id', vehicleId);
        if (error) {
            toast.error('Could not update MOT details: ' + error.message);
            return;
        }
        setVehicles((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, ...patch } : v)));
    };

    /**
     * Re-read one vehicle row after a DVSA refresh.
     *
     * The refresh rewrites a dozen MOT columns server-side, so the local copy is
     * stale the moment it returns. Re-selecting the single row is cheaper than
     * reloading the whole profile, and keeps the open MOT panel from flickering.
     */
    const handleVehicleRefreshed = async (vehicleId: string) => {
        const { data, error } = await supabase
            .from('vehicles')
            .select('*')
            .eq('id', vehicleId)
            .maybeSingle();
        if (error) {
            reportError(error, 'customer-profile.vehicle-refresh');
            return;
        }
        if (data) {
            setVehicles((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, ...data } : v)));
        }
    };

    // Notes State
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [tempNotes, setTempNotes] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);

    const handleSaveNotes = async () => {
        if (!id) return;
        setIsSavingNotes(true);
        try {
            const { error } = await supabase
                .from('customers')
                .update({ notes: tempNotes })
                .eq('id', id);

            if (error) throw error;
            setCustomer({ ...customer, notes: tempNotes });
            setIsEditingNotes(false);
        } catch (err) {
            console.error('Error saving notes:', err);
            toast.error('Failed to save notes. Please try again.');
        } finally {
            setIsSavingNotes(false);
        }
    };

    // Fetch Data
    const fetchData = async () => {
        if (!id || !activeOrgId) return;
        setLoading(true);
        try {
            // 1. Customer Details
            const { data: custData, error: custError } = await supabase
                // Decrypting view — this screen displays the customer's email and
                // phone, which live encrypted on the base table.
                .from('customers_decrypted')
                .select('*')
                .eq('id', id)
                .single();
            if (custError) throw custError;
            setCustomer(custData);

            // Org identity — "send a copy to my email" + the sign-off on templates.
            supabase
                .from('organizations')
                .select('business_email, name')
                .eq('id', activeOrgId)
                .single()
                .then(({ data: orgData }) => {
                    setBusinessEmail(orgData?.business_email || undefined);
                    setBusinessName(orgData?.name || undefined);
                });

            // 2. Vehicles
            //
            // owner_id FIRST. This used to read vehicles only through the jobs
            // table, so a vehicle that had never been jobbed did not exist as far
            // as this screen was concerned — which is every vehicle added during a
            // diagnostic or a booking. Bob Gru's Mercedes was on his diagnostic
            // and correctly carried owner_id, and the Vehicles tab still said
            // "No vehicles linked to this customer yet."
            //
            // The jobs query stays as a second source: a vehicle can be worked on
            // for a customer who does not own it (a family car, a trade sale), and
            // those should still show on the profile.
            const [ownedRes, jobRes] = await Promise.all([
                supabase
                    .from('vehicles')
                    .select('*')
                    .eq('owner_id', id)
                    .order('created_at', { ascending: false })
                    .returns<CustomerVehicle[]>(),
                supabase
                    .from('jobs')
                    .select('vehicles(*)')
                    .eq('customer_id', id)
                    .order('created_at', { ascending: false })
                    .returns<{ vehicles: CustomerVehicle | null }[]>(),
            ]);
            if (ownedRes.error) reportError(ownedRes.error, 'customer-profile.vehicles');
            if (jobRes.error) throw jobRes.error;

            // Extract and deduplicate vehicles
            const allVehicles = [
                ...(ownedRes.data ?? []),
                ...(jobRes.data?.map(j => j.vehicles).filter((v): v is CustomerVehicle => Boolean(v)) ?? []),
            ];
            const vehiclesMap = new Map<string, CustomerVehicle>();
            for (const v of allVehicles) {
                if (!vehiclesMap.has(v.id)) {
                    vehiclesMap.set(v.id, v);
                }
            }
            setVehicles(Array.from(vehiclesMap.values()));

            // 3. Financials
            const { data: transData, error: transError } = await supabase
                .from('ledger_headers')
                .select('total_gross, status, transaction_type')
                .eq('customer_id', id)
                .is('deleted_at', null);

            const { data: invoiceData, error: invoiceError } = await supabase
                .from('invoices')
                .select('balance_due')
                .eq('customer_id', id)
                .in('status', ['issued', 'partially_paid']);

            if (!transError && transData) {
                const paid = transData
                    .filter(t => t.transaction_type === 'income' && t.status === 'posted')
                    .reduce((acc, curr) => acc + Number(curr.total_gross || 0), 0);

                const owed = !invoiceError && invoiceData
                    ? invoiceData.reduce((acc, curr) => acc + Number(curr.balance_due || 0), 0)
                    : 0;

                setFinancials({ paid, owed });
            }

            // 4. Jobs — fetch a larger batch than we show at once; ActivityTab
            //    reveals more of this same array client-side ("Load more")
            //    rather than re-querying, so a customer with a long history
            //    stays cheap to fetch and just as cheap to browse.
            const { data: jobData, error: jobError } = await supabase
                .from('jobs')
                .select('*, vehicles(make, model, vrm)')
                .eq('customer_id', id)
                .order('created_at', { ascending: false })
                .limit(20);

            if (!jobError) {
                setJobs(jobData || []);
            }

            // 5. Service History (auto-created when invoices are paid)
            const { data: shData, error: shDataError } = await supabase
                .from('service_history')
                .select('*')
                .eq('customer_id', id)
                .order('service_date', { ascending: false });
            if (shDataError) reportError(shDataError, 'customer-profile.service-history');
            setServiceHistory(shData || []);

            // 6. Diagnostic reports — the inspections carried out on this
            //    customer's vehicles live on their record, not just in Workshop.
            const { data: diagData, error: diagDataError } = await supabase
                .from('diagnostic_reports')
                .select('*')
                .eq('customer_id', id)
                .order('created_at', { ascending: false });
            if (diagDataError) reportError(diagDataError, 'customer-profile.diagnostic-reports');
            setDiagnostics(diagData || []);

            // 7. Communication — every email sent to this customer. Older sends
            //    were logged before emails carried a customer_id, so match on the
            //    address too.
            //    (the address is only used when it's plain enough to be safe in
            //    a PostgREST filter expression).
            const safeEmail = /^[\w.+-]+@[\w.-]+$/.test(custData?.email || '') ? custData.email : null;
            const orFilter = [
                `related_id.eq.${id}`,
                ...(safeEmail ? [`recipient.eq.${safeEmail}`] : []),
            ].join(',');
            const { data: emailData, error: emailDataError } = await supabase
                .from('email_log')
                .select('id, subject, body, recipient, status, template_type, created_at')
                .eq('org_id', activeOrgId)
                .or(orFilter)
                .order('created_at', { ascending: false })
                .limit(50);
            if (emailDataError) reportError(emailDataError, 'customer-profile.email-log');
            setEmails(emailData || []);

        } catch (err) {
            console.error('Error fetching profile:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        setActiveTab('overview');
    }, [id, activeOrgId]);

    if (loading) return <div className="p-12 text-center text-fg-4">Loading profile...</div>;
    if (!customer) return <div className="p-12 text-center text-danger">Customer not found</div>;

    // `shortLabel` is what the phone shows. Every tab is labelled on every
    // screen size — the icon-only phone bar that used to be here made you tap
    // each tab to find out what it was.
    const TABS: {
        id: TabId; label: string; shortLabel: string; icon: React.ElementType; count?: number;
    }[] = [
        { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: LayoutGrid },
        { id: 'vehicles', label: 'Vehicles', shortLabel: 'Vehicles', icon: Car, count: vehicles.length },
        { id: 'activity', label: 'Activity', shortLabel: 'Activity', icon: History, count: jobs.length },
        { id: 'diagnostics', label: 'Diagnostics', shortLabel: 'Diags', icon: Stethoscope, count: diagnostics.length },
        { id: 'communication', label: 'Communication', shortLabel: 'Emails', icon: Send, count: emails.length },
        { id: 'service_history', label: 'Service History', shortLabel: 'History', icon: ClipboardList, count: serviceHistory.length },
    ];

    return (
        <div className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-4">

            {/* An archived customer must LOOK archived from the first glance.
                The record stays fully readable — the history is why it is kept —
                but nothing here should suggest carrying on as normal. */}
            {customer.archived_at && (
                <div className="rounded-xl border border-gold/30 bg-gold-fill/10 p-4 flex flex-wrap items-center gap-3">
                    <Archive className="w-5 h-5 text-gold shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gold">
                            Archived {new Date(customer.archived_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gold/70 mt-0.5 break-words">
                            {customer.archived_reason
                                ? customer.archived_reason
                                : 'Hidden from search and follow-ups. Their history is unchanged.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsArchiveOpen(true)}
                        className="shrink-0 min-h-[44px] px-4 rounded-lg bg-gold-fill/20 border border-gold/40 text-gold hover:bg-gold-fill/30 transition-colors font-bold text-sm flex items-center gap-2"
                    >
                        <ArchiveRestore className="w-4 h-4" />
                        Restore
                    </button>
                </div>
            )}

            {/* Header & Navigation */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                    <button
                        onClick={() => navigate('/clients')}
                        className="p-2 rounded-full bg-tint/5 hover:bg-tint/10 transition-colors shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5 text-fg-3" />
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl sm:text-3xl font-bold text-fg break-words min-w-0">{customer.full_name}</h1>
                            <button
                                onClick={() => setIsEditModalOpen(true)}
                                className="p-1.5 rounded-lg bg-tint/5 hover:bg-tint/10 text-fg-3 hover:text-fg transition-colors shrink-0"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-fg-3">
                            {customer.email && (
                                <span className="flex items-center gap-1.5 min-w-0 max-w-full">
                                    <Mail className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate">{customer.email}</span>
                                </span>
                            )}
                            {customer.phone && (
                                <span className="flex items-center gap-1.5">
                                    <Phone className="w-3.5 h-3.5 shrink-0" />
                                    {customer.phone}
                                </span>
                            )}
                            {customer.address && (
                                <span className="flex items-center gap-1.5 min-w-0 max-w-full">
                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate">{customer.address}</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:justify-end sm:shrink-0">
                    {serviceHistory.length > 0 && (
                        <button
                            onClick={() => openServiceHistory(undefined)}
                            className="fd-btn fd-btn-secondary"
                            title="Generate, print or email this customer's service history"
                        >
                            <ClipboardList className="w-5 h-5 shrink-0" />
                            <span>Service History</span>
                        </button>
                    )}
                    <button
                        onClick={() => setIsMergeOpen(true)}
                        className="fd-btn fd-btn-secondary"
                        title="Merge a duplicate of this customer into one record"
                    >
                        <GitMerge className="w-5 h-5 shrink-0" />
                        <span>Merge</span>
                    </button>
                    {/* Only offered while they are active — the way back is the
                        Restore button on the banner above, which is where
                        someone looking at an archived record will already be
                        looking. */}
                    {!customer.archived_at && (
                        <button
                            onClick={() => setIsArchiveOpen(true)}
                            className="fd-btn fd-btn-secondary"
                            title="Hide this customer from lists and stop their reminders, keeping their history"
                        >
                            <Archive className="w-5 h-5 shrink-0" />
                            <span>Archive</span>
                        </button>
                    )}
                    {/* ALWAYS rendered. This was gated on `customer.email`, so
                        for every customer created without one — which is every
                        walk-in added mid-diagnostic — the Email button simply was
                        not on the page, and it read as the feature having been
                        removed. No address on file is a reason to ask for one,
                        not a reason to hide the action. */}
                    <button
                        onClick={() => {
                            if (customer.email) { openEmail(); return; }
                            toast('Add an email address for this customer first.');
                            setIsEditModalOpen(true);
                        }}
                        className="fd-btn fd-btn-primary"
                        title={customer.email ? 'Email this customer' : 'No email on file — add one to send'}
                    >
                        <Mail className="w-5 h-5 shrink-0" />
                        <span>Email</span>
                    </button>
                    {customer.phone && (
                        <a
                            href={`tel:${customer.phone}`}
                            className="flex items-center justify-center p-2.5 rounded-lg bg-info/10 text-info hover:bg-info/20 transition-colors"
                            title="Call"
                        >
                            <Phone className="w-5 h-5" />
                        </a>
                    )}
                </div>
            </div>

            {/* `min-w-0` on both columns is load-bearing, not decoration: a grid
                item defaults to `min-width: auto`, so the six-tab scroller below
                sized this track to the full width of every tab laid end to end
                (~860px) and pushed the whole page off a phone screen. Because
                `body` clips horizontal overflow, the spill was unreachable
                rather than scrollable. */}
            {/* The tab bar spans the page and the content grid sits under it,
                so the sidebar starts level with the first card instead of
                beside the tabs. */}
            <div className="space-y-6 min-w-0">
                    {/* Two rows of three on a phone, one row from sm up.
                        Six labelled tabs are ~860px laid end to end, which is
                        why this was once a sideways scroller and then icon-only
                        — both hid what the tabs were, and the icon-only bar
                        meant tapping each one to find out.

                        The label stacks UNDER the icon below sm because it has
                        to: a 3-column grid inside a 320px viewport gives each
                        tab ~93px, and side by side the icon, gap, count badge
                        and padding leave barely 30px of that for the text.
                        Stacked, the label gets the full width and every one of
                        them reads in full. */}
                    <nav
                        aria-label="Customer sections"
                        className="grid grid-cols-3 sm:flex sm:flex-wrap gap-1 border-b border-line"
                    >
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={activeTab === tab.id ? 'page' : undefined}
                                className={`min-w-0 flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-1 sm:px-3 py-2 sm:py-2.5 min-h-[44px] font-semibold border-b-2 transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-accent text-fg'
                                        : 'border-transparent text-fg-4 hover:text-fg-2'
                                }`}
                            >
                                <span className="flex items-center gap-1 shrink-0">
                                    <tab.icon className="w-4 h-4 shrink-0" />
                                    {typeof tab.count === 'number' && tab.count > 0 && (
                                        <span className="text-fd-3xs font-bold bg-tint/10 text-fg-3 rounded-full px-1.5 leading-tight">
                                            {tab.count}
                                        </span>
                                    )}
                                </span>
                                <span className="max-w-full truncate text-fd-2xs sm:hidden">{tab.shortLabel}</span>
                                <span className="hidden sm:inline text-sm">{tab.label}</span>
                            </button>
                        ))}
                    </nav>

            <div className="grid md:grid-cols-3 gap-6 items-start">

                {/* MAIN — active tab content */}
                <div className="md:col-span-2 space-y-4 min-w-0">
                    {activeTab === 'overview' && (
                        <OverviewTab
                            vehicles={vehicles}
                            jobs={jobs}
                            emails={emails}
                            diagnosticsCount={diagnostics.length}
                            serviceHistoryCount={serviceHistory.length}
                            onSelectTab={(tab) => setActiveTab(tab as TabId)}
                        />
                    )}
                    {activeTab === 'vehicles' && (
                        <VehiclesTab
                            customerId={id!}
                            vehicles={vehicles}
                            vehiclesWithHistory={vehiclesWithHistory}
                            onAddVehicle={() => setIsAddVehicleOpen(true)}
                            onVehicleRefreshed={handleVehicleRefreshed}
                            onMotUpdate={handleMotUpdate}
                            onOpenServiceHistory={openServiceHistory}
                            onOpenEmail={openEmail}
                        />
                    )}
                    {activeTab === 'activity' && <ActivityTab jobs={jobs} />}
                    {activeTab === 'diagnostics' && (
                        <DiagnosticsTab diagnostics={diagnostics} onOpenDiagnostic={openDiagnostic} />
                    )}
                    {activeTab === 'communication' && (
                        <CommunicationTab
                            emails={emails}
                            hasEmail={!!customer.email}
                            onOpenEmail={() => openEmail()}
                        />
                    )}
                    {activeTab === 'service_history' && <ServiceHistoryTab serviceHistory={serviceHistory} />}
                </div>

                {/* SIDEBAR — a genuine sidebar only from md: up. Below that the
                    grid is one column, so this rendered UNDER whichever tab was
                    open — the financial summary reappeared on Vehicles, on
                    Communication, on Service history, every time. It belongs to
                    the overview on a phone and nowhere else. */}
                <div className={`space-y-6 min-w-0 ${activeTab === 'overview' ? '' : 'hidden md:block'}`}>
                    {/* Financial Summary */}
                    <div className="fd-card p-5 space-y-4">
                        <h3 className="fd-card-label flex items-center gap-2">
                            <Wallet className="w-4 h-4" />
                            Financial Summary
                        </h3>
                        {/* Wraps rather than tiles: this card is a narrow third
                            column on desktop and full width on a phone, and a
                            five-figure total overruns a rigid half either way. */}
                        <div className="flex flex-wrap gap-4">
                            <div className="min-w-0 flex-1 basis-32">
                                <p className="fd-kpi text-[1.75rem]">
                                    £{financials.paid.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-fd-3xs uppercase font-bold text-success mt-1">Paid / Collected</p>
                            </div>
                            <div className="min-w-0 flex-1 basis-32">
                                <p className="fd-kpi text-[1.75rem]">
                                    £{financials.owed.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-fd-3xs uppercase font-bold text-gold mt-1">Owed / Outstanding</p>
                            </div>
                        </div>
                    </div>

                    {/* Notes Field */}
                    <div className="fd-card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="fd-card-label">Internal Notes</h3>
                            {!isEditingNotes ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTempNotes(customer.notes || '');
                                        setIsEditingNotes(true);
                                    }}
                                    className="text-fd-3xs text-accent hover:underline font-bold uppercase"
                                >
                                    Edit
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditingNotes(false);
                                        }}
                                        className="text-fd-3xs text-fg-4 hover:text-fg font-bold uppercase transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveNotes();
                                        }}
                                        disabled={isSavingNotes}
                                        className="text-fd-3xs text-success hover:text-success font-bold uppercase transition-colors disabled:opacity-50"
                                    >
                                        {isSavingNotes ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingNotes ? (
                            <textarea
                                value={tempNotes}
                                onChange={(e) => setTempNotes(e.target.value)}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg p-3 text-sm text-fg placeholder-fg-4 focus:outline-none focus:border-accent/50 min-h-[120px] resize-none"
                                placeholder="Add internal notes about this customer..."
                                autoFocus
                            />
                        ) : (
                            <p className="text-sm text-fg-2 whitespace-pre-wrap">
                                {customer.notes || "No notes added."}
                            </p>
                        )}
                    </div>
                </div>

            </div>
            </div>

            {isAddVehicleOpen && (
                <AddVehicleModal
                    customerId={id!}
                    onClose={() => setIsAddVehicleOpen(false)}
                    onSuccess={() => fetchData()}
                />
            )}

            {isEditModalOpen && customer && (
                <EditCustomerModal
                    customer={customer}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={() => fetchData()}
                />
            )}

            {customer && activeOrgId && (
                <CustomerEmailModal
                    isOpen={isEmailModalOpen}
                    onClose={() => setIsEmailModalOpen(false)}
                    customerEmail={customer.email || ''}
                    customerName={customer.full_name || ''}
                    customerId={id}
                    orgId={activeOrgId}
                    businessEmail={businessEmail}
                    businessName={businessName}
                    vehicles={vehicles}
                    initialTemplateId={emailPrefill.templateId}
                    initialVehicleId={emailPrefill.vehicleId}
                    hasServiceHistory={serviceHistory.length > 0}
                    diagnosticReports={diagnostics.map((d) => ({
                        id: d.id,
                        created_at: d.created_at,
                        vehicle_id: d.vehicle_id,
                        vehicle_vrm: d.vehicle_vrm,
                        findingCount: Array.isArray(d.findings) ? d.findings.length : 0,
                    }))}
                    onSuccess={fetchData}
                />
            )}

            {activeOrgId && (
                <DiagnosticModal
                    isOpen={diagnosticOpen}
                    onClose={() => setDiagnosticOpen(false)}
                    onSaved={fetchData}
                    context={diagnosticContext}
                />
            )}

            {customer && activeOrgId && serviceHistoryOpen && (
                <ServiceHistoryModal
                    isOpen={serviceHistoryOpen}
                    onClose={() => setServiceHistoryOpen(false)}
                    customerId={id!}
                    customerName={customer.full_name || ''}
                    customerEmail={customer.email || undefined}
                    customerPhone={customer.phone || undefined}
                    orgId={activeOrgId}
                    businessEmail={businessEmail}
                    vehicleId={serviceHistoryVehicleId}
                />
            )}

            {customer && isArchiveOpen && (
                <ArchiveCustomerModal
                    onClose={() => setIsArchiveOpen(false)}
                    customer={{ id: id!, full_name: customer.full_name || '', archived_at: customer.archived_at }}
                    onDone={fetchData}
                />
            )}

            {customer && activeOrgId && (
                <MergeCustomerModal
                    isOpen={isMergeOpen}
                    onClose={() => setIsMergeOpen(false)}
                    orgId={activeOrgId}
                    currentCustomer={{ id: id!, full_name: customer.full_name || '', phone: customer.phone || null, email: customer.email || null }}
                    onMerged={(keptId) => {
                        setIsMergeOpen(false);
                        if (keptId === id) {
                            fetchData();
                        } else {
                            navigate(`/clients/${keptId}`);
                        }
                    }}
                />
            )}
        </div>
    );
}
